import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RSVPForm } from '@/components/RSVPForm';
import { useEvents } from '@/hooks/useEvents';
import { useTheme } from '@/hooks/useTheme';
import { PasswordGate } from './PasswordGate';
import { Event, RSVPPayload } from '@/types';

export function EventPage() {
  const { slug } = useParams<{ slug: string }>();
  const { getEventBySlug, verifyPassword, submitRSVP, loading, error } = useEvents();
  const { applyTheme, resetTheme } = useTheme();

  const [event, setEvent] = useState<Event | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  const handlePasswordSubmit = async (password: string) => {
    if (!slug) return;
    const valid = await verifyPassword(slug, password);
    if (valid) {
      setAuthenticated(true);
      setPasswordError('');
      // Store password in session
      sessionStorage.setItem(`event_password_${slug}`, password);
      const data = await getEventBySlug(slug, password);
      if (data) {
        setEvent(data);
        applyTheme(data.theme);
      }
    } else {
      setPasswordError('Incorrect password. Please try again.');
    }
  };

  useEffect(() => {
    if (!slug) return;
    // Check for stored password
    const storedPassword = sessionStorage.getItem(`event_password_${slug}`);
    if (storedPassword) {
      handlePasswordSubmit(storedPassword);
    }
    return () => resetTheme();
  }, [slug]);

  const handleRSVP = async (data: RSVPPayload) => {
    if (!slug) return;
    await submitRSVP(slug, data);
  };

  if (!authenticated) {
    return <PasswordGate onSubmit={handlePasswordSubmit} error={passwordError} loading={loading} />;
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600" />
      </div>
    );
  }

  const eventDate = new Date(event.date).toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className="min-h-screen" style={{ backgroundColor: event.theme.background }}>
      {/* Cover Photo */}
      {event.cover_photo_url && (
        <div className="w-full h-64 sm:h-80 lg:h-96 overflow-hidden">
          <img src={event.cover_photo_url} alt={event.name} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Event Header */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold font-display mb-4" style={{ color: event.theme.primary_color }}>
            {event.name}
          </h1>
          <p className="text-lg opacity-80 mb-2">{eventDate}</p>
          <p className="text-lg opacity-80">{event.location}</p>
        </div>

        {/* Description */}
        {event.description && (
          <div className="prose max-w-none mb-12 animate-slide-up">
            <p className="text-lg leading-relaxed opacity-90 text-center">{event.description}</p>
          </div>
        )}

        {/* Gallery */}
        {event.gallery_urls && event.gallery_urls.length > 0 && (
          <div className="mb-12 animate-slide-up">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {event.gallery_urls.map((url, i) => (
                <div key={i} className="rounded-lg overflow-hidden aspect-square">
                  <img src={url} alt={`Gallery ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RSVP Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8 animate-slide-up">
          <h2 className="text-2xl font-bold font-display text-center mb-6" style={{ color: event.theme.primary_color }}>
            RSVP
          </h2>
          <RSVPForm customFields={event.custom_fields || []} onSubmit={handleRSVP} loading={loading} allowMaybe={event.allow_maybe !== false} />
        </div>
      </div>
    </div>
  );
}
